import { NavLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAdmin } from "../contexts/AdminContext";
import { shortPrincipal } from "../lib/utils";

const navItems = [
  { to: "/home", label: "Home", icon: "⊞" },
  { to: "/shops", label: "Shops", icon: "🏪" },
  { to: "/pending", label: "Pending", icon: "⏳" },
  { to: "/registrations", label: "Registrations", icon: "📋" },
  { to: "/subscriptions", label: "Subscriptions", icon: "🔄" },
  { to: "/payments", label: "M-Pesa Payments", icon: "💳" },
  { to: "/admins", label: "Admins", icon: "👤" },
  { to: "/free-codes", label: "Free Codes", icon: "🎟" },
];

const linkStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 16px",
  borderRadius: 4,
  color: "var(--color-muted)",
  fontSize: 14,
  fontWeight: 500,
  textDecoration: "none",
  minHeight: 44,
  transition: "background 0.15s, color 0.15s",
};

const activeLinkStyle = {
  ...linkStyle,
  background: "var(--color-surface2)",
  color: "var(--color-text)",
};

export function Sidebar() {
  const { principalText, logout } = useAuth();
  const { admin, isInstaller, clear } = useAdmin();

  async function handleLogout() {
    await logout();
    clear();
  }

  const displayName = admin?.name ?? (isInstaller ? "Installer" : "Admin");

  const filteredNavItems = navItems.filter((item) => {
    if (item.to === "/admins" || item.to === "/free-codes") {
      return isInstaller; // Only show to installer
    }
    return true;
  });

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        style={{
          width: 220,
          background: "var(--color-surface)",
          borderRight: "1px solid var(--color-border)",
          display: "flex",
          flexDirection: "column",
          padding: "24px 12px 16px",
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 10,
        }}
        className="sidebar-desktop"
      >
        <div style={{ marginBottom: 32, paddingLeft: 8 }}>
          <h2
            style={{
              fontSize: 18,
              color: "var(--color-accent)",
              fontFamily: "'Syne', sans-serif",
            }}
          >
            TillFlo
          </h2>
          <span style={{ fontSize: 11, color: "var(--color-muted)" }}>
            Admin
          </span>
        </div>

        <nav
          style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              style={({ isActive }) => (isActive ? activeLinkStyle : linkStyle)}
            >
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div
          style={{
            borderTop: "1px solid var(--color-border)",
            paddingTop: 12,
            marginTop: 12,
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: "var(--color-text)",
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            {displayName}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--color-muted)",
              fontFamily: "'DM Mono', monospace",
              marginBottom: 12,
            }}
          >
            {shortPrincipal(principalText)}
          </div>
          <button
            className="btn-ghost"
            style={{ width: "100%", fontSize: 13 }}
            onClick={handleLogout}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <nav
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "var(--color-surface)",
          borderTop: "1px solid var(--color-border)",
          display: "flex",
          zIndex: 10,
        }}
        className="sidebar-mobile"
      >
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            style={({ isActive }) => ({
              flex: 1,
              display: "flex",
              flexDirection: "column" as const,
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 0",
              color: isActive ? "var(--color-accent)" : "var(--color-muted)",
              fontSize: 10,
              gap: 2,
              textDecoration: "none",
              minHeight: 56,
            })}
          >
            <span style={{ fontSize: 20 }}>{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </>
  );
}
